<template>
  <div class="compress-wrapper">
    <h2 class="section-title">GIF 压缩</h2>

    <!-- 加载提示 -->
    <loading-tip 
      :visible="isLoading" 
      :text="loadingText" 
      :subtext="loadingSubtext"
      :progress="loadingProgress"
    />

    <!-- 上传区域 -->
    <label v-if="!hasGif" class="drop-area" :class="{ hover: isDragging }" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
      拖拽或点击上传 GIF
      <div class="size-hint">{{ isMobile ? '建议不超过 8MB' : '建议不超过 20MB' }}</div>
      <input type="file" ref="gifInput" accept="image/gif" @change="handleFileChange">
    </label>

    <!-- 原始 GIF 信息 -->
    <div v-if="hasGif" class="info-card">
      <div class="info-header">
        <span class="info-title">原始 GIF</span>
        <button class="btn-reselect" @click="resetAll">重新选择</button>
      </div>
      <div class="info-content">
        <div class="info-item">
          <span class="info-label">文件大小:</span>
          <span class="info-value">{{ formatSize(originalSize) }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">尺寸:</span>
          <span class="info-value">{{ originalWidth }} × {{ originalHeight }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">帧数:</span>
          <span class="info-value">{{ totalFrames }} 帧</span>
        </div>
      </div>
      <img :src="gifUrl" alt="原始 GIF" class="preview-gif">
    </div>

    <!-- 压缩选项 -->
    <div v-if="hasGif" class="compress-options">
      <div class="option-title">压缩选项</div>
      
      <div class="option-group">
        <label class="option-label">缩放比例: <span class="option-value">{{ scalePercent }}%</span></label>
        <input type="range" v-model.number="scalePercent" min="10" max="100" step="5" class="range-input">
        <div class="range-hints">
          <span>10%</span>
          <span>100%</span>
        </div>
      </div>

      <div class="option-group">
        <label class="option-label">跳帧 (每N帧保留1帧): <span class="option-value">{{ skipFrames }}</span></label>
        <input type="range" v-model.number="skipFrames" min="1" max="5" step="1" class="range-input">
        <div class="range-hints">
          <span>不跳帧</span>
          <span>每5帧取1帧</span>
        </div>
      </div>

      <div class="option-group">
        <label class="option-label">质量 (越小越清晰): <span class="option-value">{{ quality }}</span></label>
        <input type="range" v-model.number="quality" min="1" max="30" step="1" class="range-input">
        <div class="range-hints">
          <span>最清晰</span>
          <span>最小体积</span>
        </div>
      </div>

      <div class="option-group">
        <label class="option-label">颜色数量: <span class="option-value">{{ colorCount }}</span></label>
        <input type="range" v-model.number="colorCount" min="16" max="256" step="16" class="range-input">
        <div class="range-hints">
          <span>16色</span>
          <span>256色</span>
        </div>
      </div>

      <!-- 预估信息 -->
      <div class="estimate-info">
        <span>预估帧数: {{ estimatedFrames }} 帧</span>
        <span>预估尺寸: {{ estimatedWidth }} × {{ estimatedHeight }}</span>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div v-if="hasGif" class="action-row">
      <button class="btn-primary" :disabled="isCompressing" @click="startCompress">
        {{ isCompressing ? '正在压缩...' : '开始压缩' }}
      </button>
      <button class="btn-download" :disabled="!compressedGifUrl" @click="downloadGif">下载 GIF</button>
    </div>

    <!-- 进度条 -->
    <div class="progress-container" v-if="isCompressing">
      <div class="progress-label">正在压缩 GIF... {{ compressProgress }}%</div>
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: compressProgress + '%' }"></div>
      </div>
    </div>

    <!-- 压缩结果 -->
    <div v-if="compressedGifUrl" class="result-card">
      <div class="info-header">
        <span class="info-title">压缩结果</span>
        <span class="compress-ratio" :class="{ 'good': compressionRatio > 0 }">
          {{ compressionRatio > 0 ? '减少 ' + compressionRatio + '%' : '增加 ' + Math.abs(compressionRatio) + '%' }}
        </span>
      </div>
      <div class="info-content">
        <div class="info-item">
          <span class="info-label">文件大小:</span>
          <span class="info-value highlight">{{ formatSize(compressedSize) }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">尺寸:</span>
          <span class="info-value">{{ compressedWidth }} × {{ compressedHeight }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">帧数:</span>
          <span class="info-value">{{ compressedFrames }} 帧</span>
        </div>
      </div>
      <img :src="compressedGifUrl" alt="压缩后 GIF" class="preview-gif">
    </div>
  </div>
</template>

<script>
import SuperGif from '../lib/libgif.js'
import GIF from 'gif.js'
import LoadingTip from './LoadingTip.vue'
import { isMobile, getDeviceConfig, checkFileSize, formatFileSize, yieldToMain, downloadFile, blobToBase64 } from './utils.js'

export default {
  name: 'GifCompress',
  components: {
    LoadingTip
  },
  data() {
    const config = getDeviceConfig()
    return {
      gifUrl: '',
      gifFile: null,
      gifName: '',
      originalSize: 0,
      originalWidth: 0,
      originalHeight: 0,
      totalFrames: 0,
      hasGif: false,
      isDragging: false,
      
      // 流式处理 - 不存储帧数据
      superGif: null,
      frameDelays: [],
      gifWrapper: null,
      blobUrl: null,
      
      // 压缩选项
      scalePercent: 80,
      skipFrames: 1,
      quality: config.defaultQuality,
      colorCount: 256,
      
      // 压缩状态
      isCompressing: false,
      compressProgress: 0,
      compressedGifUrl: '',
      compressedSize: 0,
      compressedWidth: 0,
      compressedHeight: 0,
      compressedFrames: 0,
      
      // 加载状态
      isLoading: false,
      loadingText: '',
      loadingSubtext: '',
      loadingProgress: -1,
      
      // 设备配置
      isMobile: config.isMobile,
      maxGifSize: config.maxGifSize,
      batchSize: config.batchSize
    }
  },
  computed: {
    estimatedFrames() {
      return Math.ceil(this.totalFrames / this.skipFrames)
    },
    estimatedWidth() {
      return Math.round(this.originalWidth * this.scalePercent / 100)
    },
    estimatedHeight() {
      return Math.round(this.originalHeight * this.scalePercent / 100)
    },
    compressionRatio() {
      if (!this.compressedSize || !this.originalSize) return 0
      return Math.round((1 - this.compressedSize / this.originalSize) * 100)
    }
  },
  methods: {
    formatSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    handleDrop(e) {
      this.isDragging = false
      const file = e.dataTransfer.files[0]
      if (file && file.type === 'image/gif') {
        this.processFile(file)
      }
    },
    handleFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.processFile(file)
      }
    },
    processFile(file) {
      // 文件大小检查
      const check = checkFileSize(file, this.maxGifSize)
      if (!check.valid) {
        this.$toast(check.message)
        return
      }
      
      this.gifFile = file
      this.gifName = file.name.replace(/\.gif$/i, '')
      this.originalSize = file.size
      this.gifUrl = URL.createObjectURL(file)
      this.hasGif = true
      this.compressedGifUrl = ''
      this.compressedSize = 0
      
      // 显示加载提示
      this.isLoading = true
      this.loadingText = '正在解析 GIF...'
      this.loadingSubtext = `文件大小: ${formatFileSize(file.size)}`
      this.loadingProgress = -1
      
      // 解析 GIF - 流式处理
      this.parseGifStream(file)
    },
    parseGifStream(file) {
      const wrapper = document.createElement('div')
      wrapper.style.cssText = 'position:fixed;left:-9999px;top:-9999px;'
      
      const gifImg = document.createElement('img')
      const blobUrl = URL.createObjectURL(file)
      gifImg.setAttribute('rel:animated_src', blobUrl)
      gifImg.setAttribute('rel:auto_play', '0')
      
      wrapper.appendChild(gifImg)
      document.body.appendChild(wrapper)
      
      const rub = new SuperGif({ gif: gifImg })
      rub.load(() => {
        this.totalFrames = rub.get_length()
        this.frameDelays = []
        
        const canvas = rub.get_canvas()
        this.originalWidth = canvas.width
        this.originalHeight = canvas.height
        
        // 只获取帧延迟信息，不存储帧数据
        const framesData = rub.get_frames()
        for (let i = 0; i < this.totalFrames; i++) {
          let delay = 100
          if (framesData && framesData[i] && framesData[i].delay) {
            delay = framesData[i].delay * 10
          }
          this.frameDelays.push(delay < 20 ? 100 : delay)
        }
        
        // 保存 SuperGif 实例用于后续处理
        this.superGif = rub
        this.gifWrapper = wrapper
        this.blobUrl = blobUrl
        
        this.isLoading = false
        console.log(`GIF 解析完成: ${this.totalFrames} 帧, ${this.originalWidth}x${this.originalHeight}`)
      })
    },
    async startCompress() {
      if (!this.superGif || this.totalFrames === 0) {
        this.$toast('请先上传 GIF')
        return
      }
      
      this.isCompressing = true
      this.compressProgress = 0
      this.compressedGifUrl = ''
      
      const newWidth = this.estimatedWidth
      const newHeight = this.estimatedHeight
      
      const gif = new GIF({
        workers: this.isMobile ? 1 : 2,
        quality: this.quality,
        workerScript: './static/gifjs/gif.worker.js',
        width: newWidth,
        height: newHeight
      })
      
      gif.on('progress', (p) => {
        this.compressProgress = Math.round(50 + p * 50)
      })
      
      // 创建可复用的 canvas
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = newWidth
      tempCanvas.height = newHeight
      const tempCtx = tempCanvas.getContext('2d')
      
      gif.on('finished', async (blob) => {
        // 使用data URL而不是blob URL，这样在小程序webview中可以长按保存
        this.compressedGifUrl = await blobToBase64(blob)
        this.compressedSize = blob.size
        this.compressedWidth = newWidth
        this.compressedHeight = newHeight
        this.isCompressing = false
        this.compressProgress = 100
        
        // 释放 canvas 内存
        tempCanvas.width = 1
        tempCanvas.height = 1
      })
      
      // 流式处理帧 - 直接从 SuperGif 读取，不存储
      let processedCount = 0
      this.compressedFrames = Math.ceil(this.totalFrames / this.skipFrames)
      
      for (let i = 0; i < this.totalFrames; i += this.skipFrames) {
        // 移动到当前帧
        this.superGif.move_to(i + 1)
        const frameCanvas = this.superGif.get_canvas()
        
        // 缩放绘制
        tempCtx.clearRect(0, 0, newWidth, newHeight)
        tempCtx.drawImage(frameCanvas, 0, 0, newWidth, newHeight)
        
        // 延迟也要相应调整
        const delay = (this.frameDelays[i] || 100) * this.skipFrames
        gif.addFrame(tempCanvas, { copy: true, delay: delay })
        
        processedCount++
        this.compressProgress = Math.round((processedCount / this.compressedFrames) * 50)
        
        // 每处理几帧让出主线程
        if (processedCount % this.batchSize === 0) {
          await yieldToMain()
        }
      }
      
      gif.render()
    },
    async downloadGif() {
      if (!this.compressedGifUrl) return
      await downloadFile(this.compressedGifUrl, `${this.gifName}_compressed.gif`)
    },
    resetAll() {
      if (this.gifUrl) {
        URL.revokeObjectURL(this.gifUrl)
      }
      if (this.compressedGifUrl) {
        URL.revokeObjectURL(this.compressedGifUrl)
      }
      if (this.gifWrapper && this.gifWrapper.parentNode) {
        this.gifWrapper.parentNode.removeChild(this.gifWrapper)
      }
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl)
      }
      
      this.gifUrl = ''
      this.gifFile = null
      this.gifName = ''
      this.originalSize = 0
      this.originalWidth = 0
      this.originalHeight = 0
      this.totalFrames = 0
      this.superGif = null
      this.frameDelays = []
      this.gifWrapper = null
      this.blobUrl = null
      this.hasGif = false
      this.compressedGifUrl = ''
      this.compressedSize = 0
      this.isCompressing = false
      this.compressProgress = 0
      this.isLoading = false
      
      if (this.$refs.gifInput) {
        this.$refs.gifInput.value = ''
      }
    }
  },
  beforeDestroy() {
    if (this.gifUrl) {
      URL.revokeObjectURL(this.gifUrl)
    }
    if (this.compressedGifUrl) {
      URL.revokeObjectURL(this.compressedGifUrl)
    }
    if (this.gifWrapper && this.gifWrapper.parentNode) {
      this.gifWrapper.parentNode.removeChild(this.gifWrapper)
    }
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl)
    }
  }
}
</script>

<style>
.compress-wrapper {
  padding: 8px 0;
}

.compress-wrapper .section-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1f2933;
}

.compress-wrapper .drop-area {
  display: flex;
  flex-direction: column;
  padding: 60px 30px;
  min-height: 180px;
  border: 2px dashed #9fb3ff;
  border-radius: 12px;
  background: #f8f9ff;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  color: #4f46e5;
  font-weight: 500;
}

.compress-wrapper .drop-area .size-hint {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 8px;
  font-weight: 400;
}

.compress-wrapper .drop-area.hover {
  border-color: #667eea;
  background: #e5edff;
}

.compress-wrapper .drop-area input[type="file"] {
  display: none;
}

.compress-wrapper .info-card,
.compress-wrapper .result-card {
  background: #f8f9ff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.compress-wrapper .result-card {
  background: #f0fff4;
  border: 1px solid #86efac;
}

.compress-wrapper .info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.compress-wrapper .info-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.compress-wrapper .btn-reselect {
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
}

.compress-wrapper .compress-ratio {
  font-size: 14px;
  font-weight: 600;
  color: #dc2626;
}

.compress-wrapper .compress-ratio.good {
  color: #16a34a;
}

.compress-wrapper .info-content {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
}

.compress-wrapper .info-item {
  display: flex;
  gap: 6px;
}

.compress-wrapper .info-label {
  font-size: 13px;
  color: #6b7280;
}

.compress-wrapper .info-value {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.compress-wrapper .info-value.highlight {
  color: #16a34a;
}

.compress-wrapper .preview-gif {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  display: block;
}

.compress-wrapper .compress-options {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.compress-wrapper .option-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
}

.compress-wrapper .option-group {
  margin-bottom: 16px;
}

.compress-wrapper .option-label {
  display: block;
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 8px;
}

.compress-wrapper .option-value {
  color: #4f46e5;
  font-weight: 600;
}

.compress-wrapper .range-input {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
}

.compress-wrapper .range-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #667eea;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.compress-wrapper .range-hints {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
}

.compress-wrapper .estimate-info {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #6b7280;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}

.compress-wrapper .action-row {
  display: flex;
  gap: 10px;
  margin: 16px 0;
}

.compress-wrapper .btn-primary {
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.compress-wrapper .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.compress-wrapper .btn-download {
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: #28a745;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.compress-wrapper .btn-download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.compress-wrapper .progress-container {
  margin: 16px 0;
  padding: 12px;
  background: #f8f9ff;
  border-radius: 8px;
}

.compress-wrapper .progress-label {
  font-size: 13px;
  color: #4f46e5;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: center;
}

.compress-wrapper .progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.compress-wrapper .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}
</style>

